import React, { createContext, useContext, ReactNode } from 'react';
import { useStatus } from './StatusProvider';

interface Entitlement {
  Client_ID: string;
  Company_ID: string;
  Admin: boolean;
  Approver: boolean;
  Executive: boolean;
}

interface EntitlementsContextType {
  entitlements: Entitlement[];
  isAdmin: (clientId?: string) => boolean;
  isApprover: (clientId?: string) => boolean;
  isExecutive: (clientId?: string) => boolean;
  hasRole: (role: 'Admin' | 'Approver' | 'Executive', clientId?: string) => boolean;
}

const EntitlementsContext = createContext<EntitlementsContextType | undefined>(undefined);

export function EntitlementsProvider({ children }: { children: ReactNode }) {
  const { User_Entitlements, Client_ID } = useStatus();

  const entitlements = User_Entitlements || [];

  const isAdmin = (clientId?: string): boolean => {
    const targetClientId = clientId || Client_ID;
    return entitlements.some(
      (e) => e.Client_ID === targetClientId && e.Admin === true
    );
  };

  const isApprover = (clientId?: string): boolean => {
    const targetClientId = clientId || Client_ID;
    return entitlements.some(
      (e) => e.Client_ID === targetClientId && e.Approver === true
    );
  };

  const isExecutive = (clientId?: string): boolean => {
    const targetClientId = clientId || Client_ID;
    return entitlements.some(
      (e) => e.Client_ID === targetClientId && e.Executive === true
    );
  };

  const hasRole = (role: 'Admin' | 'Approver' | 'Executive', clientId?: string): boolean => {
    const targetClientId = clientId || Client_ID;
    return entitlements.some(
      (e) => e.Client_ID === targetClientId && e[role] === true
    );
  };

  const value: EntitlementsContextType = {
    entitlements,
    isAdmin,
    isApprover,
    isExecutive,
    hasRole,
  };

  return (
    <EntitlementsContext.Provider value={value}>
      {children}
    </EntitlementsContext.Provider>
  );
}

export function useEntitlements() {
  const context = useContext(EntitlementsContext);
  if (context === undefined) {
    throw new Error('useEntitlements must be used within an EntitlementsProvider');
  }
  return context;
}
